import { useEffect, useCallback, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications, ScheduleOptions } from '@capacitor/local-notifications';

export interface ScheduledNotification {
  id: number;
  title: string;
  body: string;
  schedule: {
    at: Date;
    repeats?: boolean;
    every?: 'day' | 'week' | 'month';
  };
  extra?: any;
}

export const useLocalNotifications = () => {
  const [permissionStatus, setPermissionStatus] = useState<'prompt' | 'granted' | 'denied' | 'unknown'>('unknown');
  const [isInitialized, setIsInitialized] = useState(false);

  // Check permission status without requesting
  const checkPermissionStatus = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      return 'unknown';
    }

    try {
      const status = await LocalNotifications.checkPermissions();
      setPermissionStatus(status.display as any);
      return status.display;
    } catch (error) {
      console.error('Error checking local notification permission:', error);
      return 'unknown';
    }
  }, []);

  // Initialize listeners only (no permission request)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const initializeListeners = async () => {
      try {
        // Check current permission status
        await checkPermissionStatus();

        // Listen for notification actions
        await LocalNotifications.addListener('localNotificationActionPerformed', (notification) => {
          const data = notification.notification.extra;
          if (data?.route) {
            window.location.href = data.route;
          }
        });

        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing local notification listeners:', error);
      }
    };

    initializeListeners();

    return () => {
      if (Capacitor.isNativePlatform()) {
        LocalNotifications.removeAllListeners();
      }
    };
  }, [checkPermissionStatus]);

  // Request permission - called explicitly by user action
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      const permStatus = await LocalNotifications.requestPermissions();
      setPermissionStatus(permStatus.display as any);
      return permStatus.display === 'granted';
    } catch (error) {
      console.error('Error requesting local notification permissions:', error);
      return false;
    }
  }, []);

  const scheduleNotification = async (notification: ScheduledNotification) => {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    // Check permission before scheduling
    const status = await checkPermissionStatus();
    if (status !== 'granted') {
      return false;
    }

    try {
      const options: ScheduleOptions = {
        notifications: [{
          id: notification.id,
          title: notification.title,
          body: notification.body,
          schedule: notification.schedule,
          extra: notification.extra,
          sound: undefined,
          attachments: undefined,
          actionTypeId: '',
          smallIcon: 'ic_stat_icon_config_sample'
        }]
      };

      await LocalNotifications.schedule(options);
      return true;
    } catch (error) {
      console.error('Error scheduling local notification:', error);
      return false;
    }
  };

  const scheduleDailyCheckIn = async (hour: number, minute: number) => {
    const now = new Date();
    const scheduledTime = new Date(now);
    scheduledTime.setHours(hour, minute, 0, 0);
    
    if (scheduledTime <= now) {
      scheduledTime.setDate(scheduledTime.getDate() + 1);
    }

    return scheduleNotification({
      id: 1,
      title: 'Daily Check-In',
      body: 'Time to check in with yourself. How are you feeling today?',
      schedule: {
        at: scheduledTime,
        repeats: true,
        every: 'day'
      },
      extra: {
        route: '/daily-checkin',
        type: 'daily_checkin'
      }
    });
  };

  const scheduleMedicationReminder = async (
    medicationId: string,
    medicationName: string,
    hour: number,
    minute: number
  ) => {
    const now = new Date();
    const scheduledTime = new Date(now);
    scheduledTime.setHours(hour, minute, 0, 0);
    
    if (scheduledTime <= now) {
      scheduledTime.setDate(scheduledTime.getDate() + 1);
    }

    return scheduleNotification({
      id: parseInt(medicationId.split('-')[0].replace(/\D/g, '')) || Math.floor(Math.random() * 100000),
      title: 'Medication Reminder',
      body: `Time to take ${medicationName}`,
      schedule: {
        at: scheduledTime,
        repeats: true,
        every: 'day'
      },
      extra: {
        route: '/medication-tracker',
        type: 'medication',
        medicationId
      }
    });
  };

  const scheduleCustomReminder = async (
    reminderId: string,
    title: string,
    message: string,
    scheduledAt: Date,
    repeats: boolean = false
  ) => {
    return scheduleNotification({
      id: parseInt(reminderId.split('-')[0].replace(/\D/g, '')) || Math.floor(Math.random() * 100000),
      title,
      body: message,
      schedule: {
        at: scheduledAt,
        repeats,
        every: repeats ? 'day' : undefined
      },
      extra: {
        route: '/reminders',
        type: 'custom',
        reminderId
      }
    });
  };

  const cancelNotification = async (notificationId: number) => {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      await LocalNotifications.cancel({ notifications: [{ id: notificationId }] });
      return true;
    } catch (error) {
      console.error('Error canceling notification:', error);
      return false;
    }
  };

  const getPendingNotifications = async () => {
    if (!Capacitor.isNativePlatform()) {
      return [];
    }

    try {
      const result = await LocalNotifications.getPending();
      return result.notifications;
    } catch (error) {
      console.error('Error getting pending notifications:', error);
      return [];
    }
  };

  return {
    scheduleNotification,
    scheduleDailyCheckIn,
    scheduleMedicationReminder,
    scheduleCustomReminder,
    cancelNotification,
    getPendingNotifications,
    requestPermission,
    checkPermissionStatus,
    permissionStatus,
    isInitialized
  };
};
